import { useEffect, useState, useCallback } from "react";
import { Row, Col, Spin, Alert, Typography, Select, DatePicker, Button, Card } from "antd";
import { LoadingOutlined, FilterOutlined, ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { obtenerResumenDashboard }        from "../../../../services/participantes/dashboardPromotorEncuestaService";
import { obtenerEncuestasPorEncuestador } from "../../../../services/participantes/dashboardPromotorEncuestaService";
import { obtenerEncuestasPorMunicipio }   from "../../../../services/participantes/dashboardPromotorEncuestaService";
import { EncuestadoresChart }   from "./EncuestadoresChart";
import { EncuestasPorDiaChart } from "./EncuestasPorDiaChart";
import { SexoChart }            from "./SexoChart";
import { MunicipiosChart }      from "./MunicipiosChart";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Filtros vacíos (estado inicial) ─────────────────────────────────────────
const FILTROS_INICIALES = {
  municipio_id:  undefined,
  promotor_id:   undefined,
  fecha_inicio:  undefined,
  fecha_fin:     undefined,
};

export function DashboardEncuestas() {
  const [resumen,      setResumen]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // Opciones para los selects
  const [municipios,   setMunicipios]   = useState([]);
  const [encuestadores, setEncuestadores] = useState([]);
  const [loadingOpts,  setLoadingOpts]  = useState(true);

  // Filtros activos
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);

  // ── Carga las opciones de los selects una sola vez ──────────────────────────
  useEffect(() => {
    (async () => {
      try {
        // Reutilizamos los endpoints sin filtros para poblar los selects
        const [dataEnc, dataMun] = await Promise.all([
          obtenerEncuestasPorEncuestador(),
          obtenerEncuestasPorMunicipio(),
        ]);

        setEncuestadores(
          dataEnc.map((e) => ({
            value: e.promotor_id,
            label: e.encuestador,
          }))
        );

        setMunicipios(
          dataMun.map((m) => ({
            value: m.municipio_id,
            label: m.municipio,
          }))
        );
      } catch {
        // Si falla la carga de opciones no bloqueamos el dashboard
      } finally {
        setLoadingOpts(false);
      }
    })();
  }, []);

  // ── Carga el resumen cada vez que cambian los filtros ──────────────────────
  const cargarResumen = useCallback(async (filtrosActivos) => {
    try {
      setLoading(true);
      setError(null);
      const data = await obtenerResumenDashboard(filtrosActivos);
      setResumen(data);
    } catch (err) {
      setError(err?.response?.data?.message ?? err.message ?? "Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarResumen(filtros);
  }, [filtros, cargarResumen]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleMunicipio = (value) =>
    setFiltros((prev) => ({ ...prev, municipio_id: value }));

  const handleEncuestador = (value) =>
    setFiltros((prev) => ({ ...prev, promotor_id: value }));

  const handleFechas = (_, dateStrings) => {
    const [inicio, fin] = dateStrings;
    setFiltros((prev) => ({
      ...prev,
      fecha_inicio: inicio || undefined,
      fecha_fin:    fin    || undefined,
    }));
  };

  const handleLimpiar = () => setFiltros(FILTROS_INICIALES);

  const hayFiltrosActivos = Object.values(filtros).some((v) => v !== undefined);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Alert
        type="error"
        message="Error al cargar los datos"
        description={error}
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 16 }}>
        Dashboard de Encuestas RIT
      </Title>

      {/* ── Panel de filtros ─────────────────────────────────────────────── */}
      <Card
        size="small"
        style={{ marginBottom: 24, borderRadius: 8 }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col>
            <FilterOutlined style={{ color: "#024589", marginRight: 6 }} />
            <Text strong style={{ color: "#024589" }}>Filtros</Text>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              placeholder="Municipio"
              style={{ width: "100%" }}
              options={municipios}
              loading={loadingOpts}
              value={filtros.municipio_id}
              onChange={handleMunicipio}
              filterOption={(input, opt) =>
                opt.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              placeholder="Encuestador"
              style={{ width: "100%" }}
              options={encuestadores}
              loading={loadingOpts}
              value={filtros.promotor_id}
              onChange={handleEncuestador}
              filterOption={(input, opt) =>
                opt.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>

          <Col xs={24} sm={14} md={8}>
            <RangePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder={["Fecha inicio", "Fecha fin"]}
              value={
                filtros.fecha_inicio && filtros.fecha_fin
                  ? [dayjs(filtros.fecha_inicio), dayjs(filtros.fecha_fin)]
                  : null
              }
              onChange={handleFechas}
            />
          </Col>

          {hayFiltrosActivos && (
            <Col>
              <Button
                icon={<ClearOutlined />}
                onClick={handleLimpiar}
                type="text"
                danger
              >
                Limpiar
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* ── Gráficas ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 40, color: "#024589" }} spin />}
            tip="Cargando dashboard..."
          />
        </div>
      ) : (
        <Row gutter={[20, 20]}>
          {/* Fila 1 — Sexo + Municipios */}
          <Col xs={24} lg={9}>
            <SexoChart data={resumen?.sexo} />
          </Col>
          <Col xs={24} lg={15}>
            <MunicipiosChart data={resumen?.municipios} />
          </Col>

          {/* Fila 2 — Tabla encuestadores */}
          <Col xs={24}>
            <EncuestadoresChart data={resumen?.encuestadores} />
          </Col>

          {/* Fila 3 — Línea de tiempo */}
          <Col xs={24}>
            <EncuestasPorDiaChart data={resumen?.porDia} />
          </Col>
        </Row>
      )}
    </div>
  );
}
